<template>
  <PublicPageShell>
    <div class="login-shell">
      <!-- 左侧展示地面站产品气质，右侧仅承载账号登录。 -->
      <section class="login-visual">
        <div class="visual-kicker"><i></i><span>{{ $t("无人机地面控制系统") }}</span></div>
        <h1>{{ $t("任务，从这里起飞") }}</h1>
        <p>{{ $t("连接机队、任务与实时遥测，进入飞行魔方无人机地面站。") }}</p>
        <div class="radar-stage" aria-hidden="true">
          <i class="radar-ring ring-one"></i>
          <i class="radar-ring ring-two"></i>
          <i class="radar-ring ring-three"></i>
          <i class="radar-sweep"></i>
          <span><i class="iconfont icon-wurenji"></i></span>
          <b class="radar-node node-one"></b>
          <b class="radar-node node-two"></b>
          <b class="radar-node node-three"></b>
        </div>
        <div class="visual-status">
          <span><i></i>{{ $t("终端就绪") }}</span>
          <span><i></i>{{ $t("安全连接") }}</span>
        </div>
      </section>

      <el-form ref="loginForm" :model="loginForm" class="login-form" autocomplete="off" @submit.native.prevent="handleLogin">
        <!-- 表单内容独立成面板，避免右侧信息松散并增强操作聚焦。 -->
        <div class="login-form-panel">
          <div class="form-heading">
            <small><i></i>{{ $t("身份验证") }}</small>
            <h2>{{ $t("登录地面站") }}</h2>
            <p>{{ $t("使用已授权的终端账号安全接入") }}</p>
          </div>

          <div class="form-field">
            <label for="login-username">{{ $t("终端账号") }}</label>
            <el-form-item prop="username">
              <UiInput id="login-username" ref="username" v-model="loginForm.username" :placeholder="$t('请输入终端账号')" name="username" type="text" tabindex="1" autocomplete="username">
                <i slot="prefix" class="iconfont icon-yonghuziliao"></i>
              </UiInput>
            </el-form-item>
          </div>

          <div class="form-field">
            <label for="login-password">{{ $t("登录密码") }}</label>
            <el-form-item prop="password">
              <UiInput id="login-password" :key="passwordType" ref="password" v-model="loginForm.password" :type="passwordType" :placeholder="$t('请输入登录密码')" name="terminal-new-password" tabindex="2" autocomplete="new-password" @keyup.enter.native="handleLogin">
                <i slot="prefix" class="iconfont icon-suoding"></i>
                <UiIconControl slot="suffix" class="password-toggle" size="small" variant="ghost" :title="$t('切换密码显示')" :icon="passwordType === 'password' ? 'iconfont icon-biyanjing' : 'iconfont icon-yanjing'" @click="showPwd" />
              </UiInput>
            </el-form-item>
          </div>

          <UiButton class="login-submit" native-type="submit" :loading="loading" block>
            <span class="login-submit-content"><span>{{ $t("进入地面站") }}</span><i class="el-icon-right"></i></span>
          </UiButton>

          <div class="form-security">
            <span><i></i>{{ $t("加密传输") }}</span>
            <span>{{ $t("仅授权终端可访问") }}</span>
          </div>
          <div class="form-footer"><i></i><span>{{ $t("飞行魔方终端安全接入") }}</span><i></i></div>
        </div>
      </el-form>
    </div>
  </PublicPageShell>
</template>

<script>
import { t } from '@/i18n'
import PublicPageShell from '@/components/PublicPageShell'

export default {
  name: 'LoginLayout',
  components: { PublicPageShell },
  data () {
    return {
      loginForm: {
        username: this.$store.state.app.terminalPreferences.account.lastLoginAccount || '',
        password: ''
      },
      loading: false,
      passwordType: 'password'
    }
  },
  mounted () {
    window.addEventListener('pageshow', this.clearPassword)
    this.$nextTick(this.clearPassword)
  },
  beforeDestroy () {
    window.removeEventListener('pageshow', this.clearPassword)
  },
  methods: {
    // 登录密码不进入任何持久化缓存，也不接受浏览器恢复的历史表单值。
    clearPassword () {
      this.loginForm.password = ''
      const password = this.$refs.password && this.$refs.password.$el.querySelector('input')
      if (password) password.value = ''
    },
    showPwd () {
      this.passwordType = this.passwordType === 'password' ? 'text' : 'password'
      this.$nextTick(() => this.$refs.password.focus())
    },
    async handleLogin () {
      if (this.loading) return
      const username = String(this.loginForm.username || '').trim()
      if (!username || !this.loginForm.password) {
        this.$message.warning(t('请输入终端账号和登录密码'))
        return
      }

      this.loading = true
      try {
        await this.$store.dispatch('user/fetchLogin', {
          username,
          password: this.loginForm.password
        })
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.login-shell {
  width: min(1180px, calc(100vw - 60px));
  height: min(660px, calc(100vh - 110px));
  min-height: 520px;
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: minmax(0, 1.25fr) minmax(430px, .75fr);
  overflow: hidden;
  border: 1px solid var(--ui-border-color);
  border-radius: 16px;
  background: rgba(var(--ui-bg-panel-rgb), .97);
  box-shadow: 0 30px 90px rgba(var(--ui-color-black-rgb), .62),
    0 1px 0 rgba(var(--ui-color-white-rgb), .045) inset;
  backdrop-filter: blur(20px);
}

/* 公共弹层语言：主窗口顶部中央保留短亮线，不再给表单重复套卡片。 */
.login-shell::before {
  content: '';
  position: absolute;
  z-index: 3;
  top: 0;
  left: 50%;
  width: 64px;
  height: 2px;
  border-radius: 0 0 2px 2px;
  background: linear-gradient(90deg, transparent, var(--ui-color-primary) 22%, var(--ui-color-primary) 78%, transparent);
  transform: translateX(-50%);
}

.login-shell::before,
.login-shell::after {
  content: '';
  height: 1px;
  position: absolute;
  z-index: 3;
  background: linear-gradient(90deg, transparent, rgba(var(--ui-color-primary-rgb), .88), transparent);
}

.login-shell::before {
  width: 150px;
  top: -1px;
  left: 46px;
}

.login-shell::after {
  width: 100px;
  right: 46px;
  bottom: -1px;
}

.login-visual {
  position: relative;
  display: flex;
  flex-direction: column;
  padding: 54px 58px 38px;
  overflow: hidden;
  border-right: 1px solid var(--ui-border-color-subtle);
  background:
    radial-gradient(circle at 54% 60%, rgba(var(--ui-color-primary-rgb), .08), transparent 38%),
    linear-gradient(145deg, rgba(var(--ui-color-primary-rgb), .055), transparent 62%);
}

.login-visual::after {
  content: '';
  width: 430px;
  height: 430px;
  position: absolute;
  right: -245px;
  bottom: -250px;
  border: 1px solid rgba(var(--ui-color-primary-rgb), .09);
  border-radius: 50%;
  box-shadow: 0 0 0 52px rgba(var(--ui-color-primary-rgb), .018),
    0 0 0 110px rgba(var(--ui-color-primary-rgb), .012);
  pointer-events: none;
}

.visual-kicker {
  display: flex;
  align-items: center;
  gap: var(--ui-space-sm);
  color: var(--ui-text-regular);
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 1.2px;
}

.visual-kicker i {
  width: 26px;
  height: 2px;
  background: var(--ui-color-primary);
}

.login-visual h1 {
  margin: 26px 0 var(--ui-space-sm);
  color: var(--ui-text-primary);
  font-size: clamp(38px, 4vw, 52px);
  line-height: 1.08;
  letter-spacing: 2.5px;
}

.login-visual > p {
  max-width: 460px;
  margin: 0;
  color: var(--ui-text-secondary);
  font-size: 15px;
  line-height: 1.9;
}

.radar-stage {
  width: 300px;
  height: 300px;
  position: relative;
  align-self: center;
  margin: 16px 0 8px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(var(--ui-color-primary-rgb), .105), transparent 69%);
}

.radar-ring {
  position: absolute;
  border: 1px solid rgba(var(--ui-color-primary-rgb), .22);
  border-radius: 50%;
}

.ring-one { inset: 20px; }
.ring-two { inset: 64px; }
.ring-three { inset: 108px; }

.radar-stage::before,
.radar-stage::after {
  content: '';
  position: absolute;
  background: rgba(var(--ui-color-primary-rgb), .14);
}

.radar-stage::before {
  width: 1px;
  top: 12px;
  bottom: 12px;
  left: 50%;
}

.radar-stage::after {
  height: 1px;
  top: 50%;
  right: 12px;
  left: 12px;
}

.radar-sweep {
  position: absolute;
  inset: 12px;
  border-radius: 50%;
  background: conic-gradient(from 0deg, transparent 0 76%, rgba(var(--ui-color-primary-rgb), .27) 100%);
  animation: radar-rotate 4.8s linear infinite;
}

.radar-stage > span {
  width: 66px;
  height: 66px;
  position: absolute;
  top: 50%;
  left: 50%;
  z-index: 2;
  display: grid;
  place-items: center;
  color: var(--ui-color-primary);
  border: 1px solid rgba(var(--ui-color-primary-rgb), .55);
  border-radius: 50%;
  background: rgba(var(--ui-bg-control-rgb), .9);
  box-shadow: 0 0 0 8px rgba(var(--ui-color-primary-rgb), .045),
    0 0 30px rgba(var(--ui-color-primary-rgb), .2);
  transform: translate(-50%, -50%);
}

.radar-stage > span i {
  font-size: 32px;
}

.radar-node {
  width: 7px;
  height: 7px;
  position: absolute;
  z-index: 2;
  border-radius: 50%;
  background: var(--ui-color-warning);
  box-shadow: 0 0 9px rgba(var(--ui-color-warning-rgb), .75);
  animation: radar-node 1.8s ease-in-out infinite;
}

.node-one { top: 64px; right: 68px; }
.node-two { bottom: 66px; left: 48px; animation-delay: -.6s; }
.node-three { top: 140px; right: 34px; animation-delay: -1.2s; }

.visual-status {
  position: relative;
  z-index: 1;
  display: flex;
  justify-content: space-between;
  gap: var(--ui-space-sm);
  margin-top: auto;
  padding-top: var(--ui-space-md);
  color: var(--ui-text-muted);
  border-top: 1px solid rgba(var(--ui-border-color-rgb), .48);
  font-size: 12px;
  letter-spacing: 1px;
}

.visual-status span {
  display: flex;
  align-items: center;
  gap: var(--ui-space-xs);
}

.visual-status i {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: var(--ui-color-primary);
  box-shadow: 0 0 7px rgba(var(--ui-color-primary-rgb), .7);
}

.login-form {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 44px;
  box-sizing: border-box;
  background: linear-gradient(155deg, rgba(var(--ui-bg-canvas-rgb), .78), rgba(var(--ui-bg-surface-rgb), .5));
}

.login-form-panel {
  width: 100%;
  max-width: 410px;
  padding: 36px 34px 30px;
  border: 1px solid var(--ui-border-color-subtle);
  border-radius: var(--ui-radius-large);
  background: linear-gradient(160deg, rgba(var(--ui-bg-panel-elevated-rgb), .72), rgba(var(--ui-bg-panel-rgb), .82));
  box-shadow: 0 18px 50px rgba(var(--ui-color-black-rgb), .28),
    0 1px 0 rgba(var(--ui-color-white-rgb), .025) inset;
  box-sizing: border-box;
}

.form-heading {
  margin-bottom: 30px;
}

.form-heading small {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--ui-text-secondary);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 1.2px;
}

.form-heading small i {
  width: 18px;
  height: 2px;
  background: var(--ui-color-primary);
}

.form-heading h2 {
  margin: 12px 0 7px;
  color: var(--ui-text-primary);
  font-size: 30px;
  line-height: 1.2;
  letter-spacing: .5px;
}

.form-heading p {
  margin: 0;
  color: var(--ui-text-secondary);
  font-size: 14px;
  line-height: 1.6;
}

.form-field {
  margin-bottom: 18px;
}

.form-field label {
  display: block;
  margin: 0 0 8px 2px;
  color: var(--ui-text-regular);
  font-size: 13px;
  font-weight: 600;
  letter-spacing: .5px;
}

.login-form ::v-deep .el-form-item {
  width: 100%;
  margin-bottom: 0;
}

.login-form ::v-deep .el-form-item__content {
  width: 100%;
  padding-left: 0 !important;
  line-height: normal;
}

.login-form ::v-deep .el-input__inner {
  width: 100%;
  height: 54px;
  padding: 0 48px;
  color: var(--ui-text-primary);
  border: 1px solid var(--ui-border-color);
  border-radius: 10px;
  background: rgba(var(--ui-bg-control-rgb), .9);
  box-sizing: border-box;
  font-size: 16px;
  font-weight: 600;
  letter-spacing: .8px;
  box-shadow: 0 1px 0 rgba(var(--ui-color-white-rgb), .02) inset;
}

.login-form ::v-deep .el-input__inner::placeholder {
  color: var(--ui-text-disabled);
  font-size: 14px;
  font-weight: 500;
  letter-spacing: .4px;
}

.login-form ::v-deep .el-input__inner:hover {
  border-color: var(--ui-border-color-strong);
  background: var(--ui-bg-control-elevated);
}

.login-form ::v-deep .el-input__inner:focus {
  border-color: rgba(var(--ui-color-primary-rgb), .82);
  background: var(--ui-bg-control-elevated);
  box-shadow: 0 0 0 3px rgba(var(--ui-color-primary-rgb), .1),
    0 8px 22px rgba(var(--ui-color-black-rgb), .18);
}

/* Chromium/Android WebView 自动填充不得覆盖登录页的深色输入框。 */
.login-form ::v-deep .el-input__inner:-webkit-autofill,
.login-form ::v-deep .el-input__inner:-webkit-autofill:hover,
.login-form ::v-deep .el-input__inner:-webkit-autofill:focus {
  border-color: var(--ui-border-color) !important;
  -webkit-text-fill-color: var(--ui-text-primary) !important;
  caret-color: var(--ui-text-primary);
  box-shadow: 0 0 0 1000px var(--ui-bg-control) inset !important;
}

.login-form ::v-deep .el-input__prefix {
  width: 36px;
  left: 9px;
  display: grid;
  place-items: center;
  color: var(--ui-text-secondary);
}

.login-form ::v-deep .el-input__prefix .iconfont {
  font-size: 22px;
  font-weight: 400;
  line-height: 1;
}

.login-form ::v-deep .el-input__suffix {
  right: 9px;
  display: grid;
  place-items: center;
}

.password-toggle {
  color: var(--ui-text-secondary);
}

.login-submit {
  width: 100%;
  height: 54px !important;
  min-height: 54px !important;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 6px;
  padding: 0;
  color: var(--ui-text-on-bright) !important;
  border-color: var(--ui-color-primary-active) !important;
  border-radius: 10px !important;
  background: linear-gradient(105deg, var(--ui-color-primary-active), var(--ui-color-primary)) !important;
  box-sizing: border-box;
  box-shadow: 0 12px 28px rgba(var(--ui-color-primary-rgb), .17),
    0 1px 0 rgba(var(--ui-color-white-rgb), .28) inset !important;
}

/* Element UI 会把 loading 图标插到默认插槽前面；改为覆盖箭头位置，避免挤压登录文字换行。 */
.login-submit ::v-deep > .el-icon-loading {
  position: absolute;
  z-index: 2;
  top: 50%;
  left: calc(50% + 58px);
  margin: -7px 0 0;
  font-size: 14px;
}

.login-submit ::v-deep > span {
  width: 100%;
  margin-left: 0 !important;
}

.login-submit.is-loading .login-submit-content > i {
  visibility: hidden;
}

.login-submit:focus:not(:focus-visible) {
  color: var(--ui-text-on-bright) !important;
  border-color: var(--ui-color-primary-active) !important;
  background: linear-gradient(105deg, var(--ui-color-primary-active), var(--ui-color-primary)) !important;
}

.login-submit-content {
  width: 100%;
  height: 52px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--ui-space-sm);
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 1px;
  line-height: 1;
}

.login-submit-content i {
  font-size: 15px;
  transition: transform var(--ui-duration-fast) var(--ui-ease-standard);
}

.form-security {
  display: flex;
  justify-content: space-between;
  gap: var(--ui-space-sm);
  margin-top: var(--ui-space-md);
  color: var(--ui-text-muted);
  font-size: 11px;
}

.form-security span {
  display: flex;
  align-items: center;
  gap: 6px;
}

.form-security i {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: var(--ui-color-primary);
}

.form-footer {
  display: flex;
  align-items: center;
  gap: var(--ui-space-sm);
  margin-top: var(--ui-space-lg);
  color: var(--ui-text-disabled);
  font-size: 9px;
  letter-spacing: 1.5px;
  white-space: nowrap;
}

.form-footer i {
  height: 1px;
  flex: 1;
  background: var(--ui-border-color-subtle);
}

@media (hover: hover) and (pointer: fine) {
  .password-toggle:hover {
    color: var(--ui-color-primary);
  }

  .login-submit:hover {
    color: var(--ui-text-on-bright) !important;
    border-color: var(--ui-color-primary-hover) !important;
    background: linear-gradient(105deg, var(--ui-color-primary), var(--ui-color-primary-hover)) !important;
    box-shadow: 0 15px 34px rgba(var(--ui-color-primary-rgb), .22),
      0 1px 0 rgba(var(--ui-color-white-rgb), .3) inset !important;
  }

  .login-submit:hover .login-submit-content i {
    transform: translateX(3px);
  }
}

@keyframes radar-rotate {
  to { transform: rotate(360deg); }
}

@keyframes radar-node {
  50% { opacity: .35; transform: scale(.7); }
}

@media (max-height: 690px) and (min-width: 761px) {
  .login-visual { padding-top: 32px; padding-bottom: 22px; }
  .login-visual h1 { margin-top: 16px; font-size: 36px; }
  .radar-stage { width: 225px; height: 225px; margin-top: 2px; }
  .ring-one { inset: 18px; }
  .ring-two { inset: 50px; }
  .ring-three { inset: 82px; }
  .node-one { top: 49px; right: 52px; }
  .node-two { bottom: 50px; left: 36px; }
  .node-three { top: 105px; right: 26px; }
  .visual-status { padding-top: var(--ui-space-sm); }
  .login-form { padding: 20px 30px; }
  .login-form-panel { padding: 24px 28px 20px; }
  .form-heading { margin-bottom: 18px; }
  .form-heading h2 { margin-top: 8px; font-size: 27px; }
  .form-field { margin-bottom: 12px; }
  .login-form ::v-deep .el-input__inner { height: 50px; }
  .login-submit { height: 50px !important; min-height: 50px !important; }
  .login-submit-content { height: 48px; }
  .form-security { margin-top: var(--ui-space-sm); }
  .form-footer { margin-top: var(--ui-space-md); }
}

@media (max-width: 900px) {
  .login-shell {
    width: min(94vw, 620px);
    height: auto;
    min-height: 0;
    grid-template-columns: 1fr;
  }

  .login-visual {
    display: none;
  }

  .login-form {
    min-height: min(590px, calc(100vh - 110px));
    padding: clamp(var(--ui-space-lg), 6vw, 42px);
  }
}

@media (max-width: 520px) {
  .login-shell {
    width: calc(100vw - 30px);
    border-radius: var(--ui-radius-large);
  }

  .login-form {
    min-height: 500px;
    padding: var(--ui-space-md);
  }

  .login-form-panel {
    padding: 28px 22px 24px;
  }

  .form-heading h2 { font-size: 26px; }
  .form-security { display: none; }
}
/* 登录表单直接属于主窗口，避免“窗口里面再套窗口”的重复层级。 */
.login-form-panel {
  max-width: 410px;
  padding: 0;
  border: 0;
  border-radius: 0;
  background: transparent;
  box-shadow: none;
}

/* 登录动作复用站内青绿色描边按钮语言，去掉独有的满色渐变。 */
.login-submit {
  color: var(--ui-color-primary) !important;
  border-color: rgba(var(--ui-color-primary-rgb), .78) !important;
  background: rgba(var(--ui-color-primary-rgb), .07) !important;
  box-shadow: var(--ui-shadow-control) !important;
}

.login-submit::before,
.login-submit::after {
  display: none !important;
}

.login-submit-content {
  color: inherit;
}

@media (hover: hover) and (pointer: fine) {
  .login-submit:hover {
    color: var(--ui-text-primary) !important;
    border-color: var(--ui-color-primary) !important;
    background: rgba(var(--ui-color-primary-rgb), .14) !important;
    box-shadow: 0 0 0 3px rgba(var(--ui-color-primary-rgb), .08) !important;
  }
}

/* 强制横屏手机按可用高度切换紧凑单栏，避免高分辨率手机误套用 Pad 双栏。 */
@media (orientation: landscape) and (max-height: 640px) {
  .login-shell {
    width: min(540px, calc(100vw - 100px));
    height: auto;
    min-height: 0;
    grid-template-columns: 1fr;
  }

  .login-visual { display: none; }
  .login-form { min-height: 0; padding: 14px 24px; }
  .login-form-panel { max-width: 420px; }
  .form-heading { margin-bottom: 12px; }
  .form-heading small { display: none; }
  .form-heading h2 { margin: 0 0 3px; font-size: 23px; }
  .form-heading p { font-size: 12px; line-height: 1.35; }
  .form-field { margin-bottom: 9px; }
  .form-field label { margin-bottom: 4px; font-size: 12px; }
  .login-form ::v-deep .el-input__inner { height: 44px; font-size: 15px; }
  .login-submit { height: 44px !important; min-height: 44px !important; margin-top: 2px; }
  .login-submit-content { height: 42px; }
  .form-security { margin-top: 7px; }
  .form-footer { display: none; }
}

@media (orientation: landscape) and (max-height: 420px) {
  .login-shell { width: min(520px, calc(100vw - 92px)); }
  .login-form { padding-top: 10px; padding-bottom: 10px; }
  .form-security { display: none; }
}
</style>
